import { useId } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@kidshome/ui';
import { useFocusTrap } from '@/shared/lib/use-focus-trap';
import { usePortalTarget } from '@/shared/ui/portal-host';

/**
 * Responsive ActionSheet (ADR-0033, tier 2) — the home for SHORT, self-contained actions (≤ ~4 fields,
 * no sub-pickers): set PIN, assign roles, invite, place hold, adjust balance, deposit / withdraw /
 * redeem / skip. A **bottom sheet on mobile / centred panel on desktop** behind one API (the shadcn
 * idiom), so the list behind stays a glance away on the small screen this app is built for.
 *
 * Mirrors {@link Dialog}'s accessible-modal behaviour — labelled by its title, Escape/scrim/back/X
 * dismissal, focus moved in on open + TRAPPED, focus restored to the trigger on close, reduced-motion
 * users get no entrance slide. A visible **X** close button is always rendered (NN/G: don't rely on a
 * grab handle). It NEVER hosts a content `*Form` and NEVER opens another surface (the anti-stacking
 * rule) — sub-tasks belong on the edit routes, one level deep, ever.
 */
export function ActionSheet({
  open,
  onClose,
  title,
  description,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  /** Optional sticky action row (e.g. a primary + cancel) pinned at the bottom of the sheet. */
  footer?: React.ReactNode;
}): React.ReactElement | null {
  const { t } = useTranslation('common');
  const { panelRef, onKeyDown } = useFocusTrap(open, onClose);
  const titleId = useId();
  const descId = useId();
  // The persona shell's in-scope host (else `document.body`): keeps the modal OUT of any ancestor
  // stacking/opacity/transform context — a locked card at `opacity-70` must not bleed onto this fixed
  // overlay — WHILE still rendering inside the app's theme scope, so its `--color-*` mode and the kid
  // shell's bespoke vars (`--ink`, `--surface`, …) resolve to the page's values, not the OS scheme.
  const portalTarget = usePortalTarget();

  if (!open) return null;

  return createPortal(
    // Scrim: items-end pins the sheet to the bottom on mobile; sm:items-center centres it on desktop.
    // A 60% scrim + subtle blur clearly separates the sheet from a busy page (esp. the centred desktop
    // variant, where the panel is narrower than the viewport and the page would otherwise read through).
    <div
      className="overlay-fade fixed inset-0 z-50 flex items-end justify-center bg-text/60 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        className={cn(
          'sheet-rise flex max-h-[85vh] w-full flex-col bg-surface shadow-lg outline-none',
          // Mobile: full-width sheet rounded only at the top, slides up. Desktop: a centred card.
          'rounded-t-card sm:max-w-md sm:rounded-card',
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border p-5 pb-3">
          <div className="min-w-0">
            <h2 id={titleId} className="font-display text-lg font-bold text-text">
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-1 text-sm text-text-muted">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('actions.close')}
            className="-me-1 -mt-1 shrink-0 rounded-md p-1.5 text-text-muted outline-none transition-colors hover:bg-surface-muted hover:text-text focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span aria-hidden="true">{'✕'}</span>
          </button>
        </div>

        <div className="flex flex-col gap-3 overflow-y-auto p-5 pt-4">{children}</div>

        {footer && <div className="border-t border-border p-5 pt-3">{footer}</div>}
      </div>
    </div>,
    portalTarget,
  );
}
